import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { assets } from '../../../core/constants/assets.const';
import { EnquiryService } from '../../../services/enquiries.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { driverMapService } from '../../../services/driverMap.service';
import { EncryptionService } from '../../../services/encryption.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { enquiry, enquiryResponse } from '../../../models/enquiries';
import { SocketsService } from '../../../services/sockets.service';
import { AdminSosComponent } from '../../../modals/admin-sos/admin-sos.component';
import { DialogService } from 'primeng/dynamicdialog';
import { CommonResponse } from '../../../models/common';
import { trackingMapService } from '../../../services/tackingMap.service';
import { TrackingCoords } from '../../../models/enquiries';
import { aiService } from '../../../services/ai.service';
import { AuthService } from '../../../services/auth.service';
import { DriverActiveToggleComponent } from '../driver-active-toggle/driver-active-toggle.component';

@Component({
  selector: 'logibids-map-widgets',
  imports: [DriverActiveToggleComponent],
  templateUrl: './map-widgets.component.html',
  styleUrl: './map-widgets.component.scss',
})
export class MapWidgetsComponent {
  assetsConst = assets;
  enquiryShow: boolean = false;
  isHeatFlagMap = signal<boolean>(false);

  trackingId: string = '';
  dataWidget = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  driverHomeCoords: any;
  sos = signal<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any;
  protected auctions = signal<enquiry[]>([]);
  readonly isTrackingRoute = signal<boolean>(false);
  readonly isDriverHome = signal<boolean>(false);
  readonly isVisible = computed(
    () => this.isTrackingRoute() || this.isDriverHome()
  );
  ref: import('primeng/dynamicdialog').DynamicDialogRef;
  showSoS = signal<boolean>(false);
  trackingCoords = signal<TrackingCoords | null>(null);
  private platformId = inject(PLATFORM_ID);
  accessToken = signal<string | null>(null);
  private authService = inject(AuthService);
  readonly isBrowser = isPlatformBrowser(this.platformId);
  private router = inject(Router);

  constructor(
    private _EnquiryService: EnquiryService,
    private _driverMapService: driverMapService,
    private _encryption: EncryptionService,
    private destroyRef: DestroyRef,
    private _SocketsService: SocketsService,
    private dialogService: DialogService,
    private _trackingMapService: trackingMapService,
    private _aiService: aiService
  ) {
    this.user = this._encryption.decryptLocalStorage('profile');
    this.syncRouteFlags(this.router.url);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => this.syncRouteFlags(event.urlAfterRedirects));

    effect(() => {
      if (this.isDriverHome()) {
        this._driverMapService.setHeatMap(true);
        this._driverMapService.setFlagMap(false);
      }
    });

    effect(onCleanup => {
      if (!this.isTrackingRoute()) {
        return;
      }

      this.getAuctions();
      const subscription = this._SocketsService
        .newEnquiryListner()
        .subscribe(res => {
          if (res.schedule_type === 'SCHEDULE' && res.status === 'RUNNING') {
            this.auctions.set([res, ...this.auctions()]);
          }
        });

      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      const data = this._driverMapService.getDriverHomeCoord();
      if (data !== null) {
        this.driverHomeCoords = data();
      }

      const dataWidget = this._driverMapService.getWidget();
      if (dataWidget !== null) {
        this.dataWidget = dataWidget();
      }
    });

    effect(() => {
      const data = this._driverMapService.getFlag();
      if (data !== null && data() === true) {
        this.isHeatFlagMap.set(true);
        this._driverMapService.setFlagMap(true);
        this._driverMapService.setHeatMap(false);
      }
    });

    effect(() => {
      const data = this._trackingMapService.getSos();
      if (data !== null && data() === true) {
        this.showSoS.set(data());
      }
    });

    effect(() => {
      const data = this._trackingMapService.getCoordinates();
      if (data !== null && data() !== null) {
        this.trackingCoords.set(data());
      }
    });

    effect(() => {
      const data = this._EnquiryService.driverEnquiryAll();
      if (data !== null) {
        this.enquiryShow = data.showList;
      }
    });

    effect(() => {
      if (!this.isBrowser) return;
      const token = localStorage['accessToken_sp'];
      if (this.authService.profileDetails()?.token) {
        this.accessToken.set(this.authService.profileDetails()?.token);
      } else {
        this.accessToken.set(token || undefined);
      }
    });
  }

  private syncRouteFlags(url: string): void {
    this.isTrackingRoute.set(url.startsWith('/sp-bids/tracking/'));
    this.isDriverHome.set(url.startsWith('/driver-home'));
  }

  getTrackingId(): string | null {
    const url = this.router.url; // "/sp-bids/tracking/14856"
    const segments = url.split('/');

    const id = segments[segments.length - 1];
    return id;
  }

  getAuctions() {
    const query = `schedule_type=SCHEDULE&status=RUNNING`;
    this._EnquiryService
      .getEnqueries(query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: enquiryResponse) => {
          this.auctions.set(res.list);
        },
      });
  }

  onMarkerClick(): void {
    const coords = this.trackingCoords();
    if (coords) {
      this.openGoogleMapsDirection(coords);
    }
  }

  private openGoogleMapsDirection(coords: TrackingCoords): void {
    if (isPlatformBrowser(this.platformId)) {
      const origin = `${coords.origin.lat},${coords.origin.lng}`;
      const destination = `${coords.destination.lat},${coords.destination.lng}`;

      let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;

      if (coords.waypoints && coords.waypoints.length > 0) {
        const waypointsStr = coords.waypoints
          .map(wp => `${wp.lat},${wp.lng}`)
          .join('|');
        url += `&waypoints=${waypointsStr}`;
      }

      window.open(url, '_blank');
    }
  }

  showHeatFlashMap(): void {
    this.isHeatFlagMap.set(!this.isHeatFlagMap());
    if (this.isHeatFlagMap() === true) {
      this.driverHomeCoords = null;
      this._driverMapService.setFlagMap(true);
      this._driverMapService.setHeatMap(false);
    } else {
      this._driverMapService.setHeatMap(true);
      this._driverMapService.setFlagMap(false);
    }
  }

  showEnquiries(): void {
    this.enquiryShow = !this.enquiryShow;
    this._EnquiryService.setDriverSOcketDataAllEnquiry({
      enquiry: null,
      showList: this.enquiryShow,
      all: true,
    });
  }

  btn_showDirection(): void {
    const coords = this.trackingCoords();
    if (coords) {
      this.openGoogleMapsDirection(coords);
    } else if (this.driverHomeCoords?.lat && this.driverHomeCoords?.lng) {
      if (isPlatformBrowser(this.platformId)) {
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=Current+Location&destination=${this.driverHomeCoords.lat},${this.driverHomeCoords.lng}&travelmode=driving`;
        window.open(googleMapsUrl, '_blank');
      }
    }
  }

  btn_show(): void {
    this.ref = this.dialogService.open(AdminSosComponent, {
      header: '',
      width: '95%',
      modal: true,
      closable: false,
      styleClass: 'ride_complet_modal pause_ride_modal',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((res: string | null) => {
      if (res === 'yes') {
        this.request();
      }
    });
  }

  request(): void {
    this._EnquiryService
      .adminRequest(this.getTrackingId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: CommonResponse) => {},
      });
  }

  btn_sos(): void {
    this.sos.set(!this.sos());
  }

  openAI(): void {
    this._aiService.setData(true);
  }
}
